import { z } from 'zod';
import { SubscriptionSchema } from './subscription.js';

export const PropertyTypeSchema = z.enum(['vacant_plot', 'agricultural_land']);
export type PropertyType = z.infer<typeof PropertyTypeSchema>;

export const PropertyAreaUnitSchema = z.enum(['sqft', 'acre', 'sqyd']);
export type PropertyAreaUnit = z.infer<typeof PropertyAreaUnitSchema>;

export const AreaSchema = z.object({
  value: z.number(),
  unit: PropertyAreaUnitSchema,
});
export type Area = z.infer<typeof AreaSchema>;

export const AddressSchema = z.object({
  line1: z.string(),
  line2: z.string().optional(),
  city: z.string(),
  district: z.string(),
  state: z.string(),
  pincode: z.number(),
});
export type Address = z.infer<typeof AddressSchema>;

export const ContactSchema = z.object({
  name: z.string(),
});
export type Contact = z.infer<typeof ContactSchema>;

export const OrganizationSchema = z.object({
  title: z.string(),
  address: AddressSchema,
});
export type Organization = z.infer<typeof OrganizationSchema>;

export const CoordinatesSchema = z.object({
  lat: z.number(),
  lon: z.number(),
});
export type Coordinates = z.infer<typeof CoordinatesSchema>;

export const PropertyStatusSchema = z.enum(['secure', 'attention', 'pending']);
export type PropertyStatus = z.infer<typeof PropertyStatusSchema>;

export const PropertySchema = z.object({
  propertyId: z.string(),
  name: z.string(),
  type: PropertyTypeSchema,
  polygon: z.array(CoordinatesSchema),
  accessPin: CoordinatesSchema,
  status: PropertyStatusSchema,
  lastVisit: z.string(),
  createdAt: z.string(),
  area: AreaSchema,
  address: AddressSchema,
  owner: z.union([OrganizationSchema, ContactSchema]),
  ownerNotes: z.string().optional(),
  subscription: SubscriptionSchema.nullable(),
});
export type Property = z.infer<typeof PropertySchema>;

// Organization is tried first: it has stricter required fields (title + address).
// If those are absent, Zod falls through to Contact (which requires name).
const OwnerSchema = z.union([OrganizationSchema, ContactSchema]);

export const CreatePropertyRequestSchema = z.object({
  name: z.string(),
  owner: OwnerSchema,
  address: AddressSchema,
  type: PropertyTypeSchema,
  polygon: z.array(CoordinatesSchema),
  accessPin: CoordinatesSchema,
  area: AreaSchema,
  planCode: z.string(),
  /** ISO alpha-2 — the payer's billing country, not the land's location. */
  countryCode: z.string().length(2),
  ownerNotes: z.string().optional(),
});
export type CreatePropertyRequest = z.infer<typeof CreatePropertyRequestSchema>;

export const CreatePropertyResponseSchema = PropertySchema.extend({
  /** Null when checkout creation failed — the property is still readable/PENDING_PAYMENT
   *  either way. */
  checkout: z.object({ url: z.string(), expiresAt: z.string().nullable() }).nullable(),
});
export type CreatePropertyResponse = z.infer<typeof CreatePropertyResponseSchema>;

export const UpdatePropertyRequestSchema = z.object({
  name: z.string().optional(),
  owner: OwnerSchema.optional(),
  ownerNotes: z.string().optional(),
});
export type UpdatePropertyRequest = z.infer<typeof UpdatePropertyRequestSchema>;

export const RecordDemandSignalRequestSchema = CoordinatesSchema;
export type RecordDemandSignalRequest = Coordinates;

/**
 * The property's current open task, if any — surfaces monitoring progress
 * before a report exists. 'active' = agent visit not yet done; 'in_review'
 * = report submitted, awaiting admin approval.
 */
export const PropertyActiveTaskSchema = z.object({
  status: z.enum(['active', 'in_review']),
  dueDate: z.string(),
});
export type PropertyActiveTask = z.infer<typeof PropertyActiveTaskSchema>;
